import { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { stj, jts } from '@chronos/time-util';
import Button from '../Button';
import DeleteConfirmPopover from '../DeleteConfirmPopover';
import EditIcon from '../Icons/EditIcon';
import DoneIcon from '../Icons/DoneIcon';
import FormSelect from '../FormSelect';
import * as S from './styled';

const EstimateFormItem = ({
  users,
  estimate,
  handleDelete,
  editMode,
  handleEditMode,
  handleSave,
  showEditButtons = true,
  filterOptions,
  defaultValues = {},
}) => {
  const estimateInput = useRef(null);
  const [assignToId, setAssignToId] = useState(
    estimate?.accountId || defaultValues.assignToId
  );
  const [estimateStr, setEstimateStr] = useState(
    estimate ? stj(estimate.seconds) : ''
  );

  const [estimateError, setEstimateError] = useState('');
  const [assignToError, setAssignToError] = useState('');

  const validate = useCallback(() => {
    const wrongTimeFormat = estimateStr === '' || !jts(estimateStr);

    if (assignToId && !wrongTimeFormat) {
      return true;
    }
    if (!assignToId) {
      setAssignToError('This field is required');
    }
    if (wrongTimeFormat) {
      setEstimateError('Wrong time format');
    }

    return false;
  }, [estimateStr, assignToId]);

  const handleEstimateChange = useCallback((e) => {
    setEstimateStr(e.target.value);
  }, []);

  const handleUserChange = useCallback((value) => {
    estimateInput.current.focus();
    setAssignToId(value);
  }, []);

  if (!editMode && estimate) {
    const user = users.find((item) => item.id === estimate.accountId);
    return (
      <S.Row>
        <S.UserName>{user?.fullName}</S.UserName>
        <S.InputReview>{stj(estimate.seconds)}</S.InputReview>
        {showEditButtons && (
          <>
            <Button
              withIcon
              spacing="none"
              appearance="subtle-link"
              onClick={() => {
                handleEditMode();
              }}
            >
              <EditIcon size={['24px', '24px']} />
            </Button>
            <DeleteConfirmPopover
              title="Are you sure you want to delete this estimate?"
              shouldShowBorder
              preferPlace="left"
              place="left"
              onDeleteConfirm={() => {
                if (handleDelete) {
                  handleDelete(estimate.id);
                }
              }}
            />
          </>
        )}
      </S.Row>
    );
  }

  return (
    <S.Row>
      <FormSelect
        items={users}
        getValue={({ id }) => id.toString()}
        getLabel={({ fullName }) => fullName}
        onChange={handleUserChange}
        autoFocus={!defaultValues.assignToId && !estimate}
        openMenuOnFocus
        value={assignToId}
        filterOptions={(options) => {
          return filterOptions(options, estimate?.accountId);
        }}
        isInvalid={assignToError !== ''}
      />

      <S.Input
        value={estimateStr}
        autoFocus={!!defaultValues.assignToId || !!estimate}
        onChange={handleEstimateChange}
        placeholder="2h 10m"
        ref={estimateInput}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            if (!validate()) {
              return;
            }

            handleSave({
              id: estimate?.id,
              accountId: assignToId,
              seconds: jts(estimateStr),
            });
          }
        }}
        required
        isInvalid={estimateError !== ''}
      />
      <Button
        withIcon
        spacing="none"
        appearance="subtle-link"
        onClick={() => {
          if (!validate()) {
            return;
          }

          handleSave({
            id: estimate?.id,
            accountId: assignToId,
            seconds: jts(estimateStr),
          });
        }}
      >
        <DoneIcon
          size="large"
          shouldPadding
        />
      </Button>
    </S.Row>
  );
};

EstimateFormItem.defaultProps = {
  users: [],
  filterOptions: (options) => options,
  defaultValues: {},
  showEditButtons: true,
  estimate: null,
  editMode: false,
};

EstimateFormItem.propTypes = {
  users: PropTypes.array,
  filterOptions: PropTypes.func,
  defaultValues: PropTypes.shape({
    assignToId: PropTypes.string,
    seconds: PropTypes.number,
  }),
  showEditButtons: PropTypes.bool,
  estimate: PropTypes.shape({
    id: PropTypes.number,
    seconds: PropTypes.number.isRequired,
    accountId: PropTypes.string.isRequired,
  }),
  handleSave: PropTypes.func.isRequired,
  handleEditMode: PropTypes.func.isRequired,
  handleDelete: PropTypes.func,
  editMode: PropTypes.bool,
};

export default EstimateFormItem;
